export enum PostalCodeRuleType {
  EXACT_NUMERIC = 'EXACT_NUMERIC',
  MAX_LENGTH = 'MAX_LENGTH',
  COUNTRY_SPECIFIC = 'COUNTRY_SPECIFIC',
  NO_CHECK = 'NO_CHECK',
}

export interface PostalCodeRule {
  required: boolean;
  ruleType: PostalCodeRuleType;
  length?: number;
  pattern?: RegExp;
  isNumeric?: boolean;
  formatDescription: string;
}

/**
 * Postal code validation rules based on SAP ISO-standard country postal code formats.
 * Only includes countries that have specific validation requirements.
 * Countries not listed here have no postal code validation requirements.
 */
export const POSTAL_CODE_RULES: Record<string, PostalCodeRule> = {
  // === Exact numeric length rules ===
  // Armenia
  AM: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },
  // Australia
  AU: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },
  // Bangladesh
  BD: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },
  // Belarus
  BY: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 6,
    isNumeric: true,
    formatDescription: '6 digits',
  },
  // Switzerland
  CH: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },
  // Germany
  DE: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Algeria
  DZ: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Estonia
  EE: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Guadeloupe
  GP: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Guatemala
  GT: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Israel
  IL: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 7,
    isNumeric: true,
    formatDescription: '7 digits',
  },
  // Kyrgyzstan
  KG: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 6,
    isNumeric: true,
    formatDescription: '6 digits',
  },
  // Liechtenstein
  LI: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },
  // Lesotho
  LS: {
    required: false,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 3,
    isNumeric: true,
    formatDescription: '3 digits',
  },
  // Monaco
  MC: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Mexico
  MX: {
    required: false,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Nepal
  NP: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // New Zealand
  NZ: {
    required: false,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },
  // Oman
  OM: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 3,
    isNumeric: true,
    formatDescription: '3 digits',
  },
  // Philippines
  PH: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },
  // St. Pierre and Miquelon
  PM: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Thailand
  TH: {
    required: false,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Tajikistan
  TJ: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 6,
    isNumeric: true,
    formatDescription: '6 digits',
  },
  // Turkmenistan
  TM: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 6,
    isNumeric: true,
    formatDescription: '6 digits',
  },
  // Ukraine
  UA: {
    required: true,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 5,
    isNumeric: true,
    formatDescription: '5 digits',
  },
  // Venezuela
  VE: {
    required: false,
    ruleType: PostalCodeRuleType.EXACT_NUMERIC,
    length: 4,
    isNumeric: true,
    formatDescription: '4 digits',
  },

  // === Maximum length rules ===
  // American Samoa
  AS: {
    required: true,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 10,
    formatDescription: 'Up to 10 characters',
  },
  // Faroe Islands
  FO: {
    required: false,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 5,
    isNumeric: true,
    formatDescription: 'Up to 5 digits',
  },
  // Honduras
  HN: {
    required: true,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 6,
    formatDescription: 'Up to 6 characters',
  },
  // Japan
  JP: {
    required: false,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 8,
    formatDescription: 'Up to 8 characters',
  },
  // North Korea
  KP: {
    required: false,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 7,
    formatDescription: 'Up to 7 characters',
  },
  // South Korea
  KR: {
    required: true,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 7,
    isNumeric: true,
    formatDescription: 'Up to 7 digits',
  },
  // Latvia
  LV: {
    required: true,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 7,
    formatDescription: 'Up to 7 characters',
  },
  // United States
  US: {
    required: true,
    ruleType: PostalCodeRuleType.MAX_LENGTH,
    length: 10,
    formatDescription: 'Up to 10 characters (e.g., 12345-6789)',
  },

  // === Country-specific format rules ===
  // Andorra
  AD: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 5,
    pattern: /^AD\d{3}$/i,
    formatDescription: 'AD### (e.g., AD500)',
  },
  // Bermuda
  BM: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 5,
    pattern: /^[A-Z]{2}\s[A-Z0-9]{2}$/i,
    formatDescription: 'AA ## or AA AA (e.g., HM 12, CR AX)',
  },
  // Brazil
  BR: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 9,
    pattern: /^\d{5}-?\d{3}$/,
    formatDescription: '#####-### (e.g., 01310-100)',
  },
  // Canada
  CA: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 7,
    pattern:
      /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
    formatDescription: 'A1A 1A1',
  },
  // Ecuador
  EC: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 6,
    pattern: /^\d{6}$/,
    formatDescription: '6 digits',
  },
  // United Kingdom
  GB: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 8,
    pattern: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s\d[A-Z]{2}$/i,
    formatDescription: 'UK format (e.g., SW1A 1AA)',
  },
  // Mauritius
  MU: {
    required: false,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 5,
    pattern: /^[A-Z0-9]{5}$/i,
    formatDescription: '5 alphanumeric characters',
  },
  // Nicaragua
  NI: {
    required: false,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 5,
    pattern: /^\d{5}$/,
    formatDescription: '5 digits',
  },
  // Panama
  PA: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 4,
    pattern: /^\d{4}$/,
    formatDescription: '4 digits',
  },
  // Sweden
  SE: {
    required: true,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 6,
    pattern: /^\d{3}\s?\d{2}$/,
    formatDescription: '### ## (e.g., 123 45)',
  },
  // Somalia
  SO: {
    required: false,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 9,
    pattern: /^[A-Z]{2}\s?\d{5}$/i,
    formatDescription: 'AA ##### (e.g., JH 09010)',
  },
  // eSwatini
  SZ: {
    required: false,
    ruleType: PostalCodeRuleType.COUNTRY_SPECIFIC,
    length: 4,
    pattern: /^[A-Z]\d{3}$/i,
    formatDescription: 'A### (e.g., H100)',
  },
};

/**
 * Gets the postal code validation rule for a given country code.
 * @param countryCode - ISO 2-character country code
 * @returns PostalCodeRule if the country has specific validation requirements, undefined otherwise
 */
export const getPostalCodeRule = (
  countryCode: string
): PostalCodeRule | undefined => {
  return POSTAL_CODE_RULES[countryCode?.toUpperCase()];
};
